import { AnimatedValue } from '../../../shared/components/AnimatedValue';
import { TrendIndicator } from '../../../shared/components/TrendIndicator';
import { SensitiveValue } from '@/components/privacy/SensitiveText';

interface KPICardProps {
  label: string;
  value: string;
  subtitle?: string;
  change?: number | null;
  /** Previous value for TrendIndicator comparison (if provided, enables the new TrendIndicator) */
  previousValue?: number | null;
  /** Current numeric value for TrendIndicator comparison */
  currentValue?: number;
  icon: string;
  active?: boolean;
  onClick?: () => void;
  /** Optional numeric value for animation (if provided, uses AnimatedValue instead of static text) */
  numericValue?: number;
  /** Format for animated value: 'currency' | 'number' | 'percent' | 'compact-currency' */
  animationFormat?: 'currency' | 'number' | 'percent' | 'compact-currency';
}

export function KPICard({
  label,
  value,
  subtitle,
  change,
  previousValue,
  currentValue,
  icon,
  active,
  onClick,
  numericValue,
  animationFormat,
}: KPICardProps): JSX.Element {
  // Determine if we should show the trend indicator
  // Use TrendIndicator if currentValue and previousValue are provided, otherwise fallback to legacy change prop
  const useTrendIndicator = currentValue !== undefined && previousValue !== undefined;
  const showLegacyChange = !useTrendIndicator && change !== null && change !== undefined && !isNaN(change) && Math.abs(change) >= 0.1;
  const isPositive = showLegacyChange && change! > 0;
  const isNegative = showLegacyChange && change! < 0;

  return (
    <div
      onClick={onClick}
      className={`
        flex flex-col p-4 rounded-monumenta min-w-[140px] transition-all duration-150
        ${active
          ? 'bg-brand-primary/10 border-2 border-brand-primary shadow-monumenta-brand'
          : 'bg-brand-dark-light border border-brand-dark-lighter shadow-monumenta'
        }
        ${onClick ? 'cursor-pointer hover:bg-brand-dark-lighter' : 'cursor-default'}
      `}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{icon}</span>
        <span className="text-xs text-brand-gray uppercase tracking-wide font-medium font-display">
          {label}
        </span>
      </div>
      <div className="flex items-baseline gap-2" data-testid="kpi-card">
        <span className={`text-2xl font-bold font-display ${active ? 'text-brand-primary' : 'text-white'}`}>
          {numericValue !== undefined && animationFormat ? (
            <AnimatedValue
              value={numericValue}
              format={animationFormat}
              className="animated-value"
            />
          ) : (
            // Wrap currency values with privacy protection - detect R$ pattern
            value.includes('R$') ? (
              <SensitiveValue mask="R$ ••••••">{value}</SensitiveValue>
            ) : (
              value
            )
          )}
        </span>
        {useTrendIndicator && (
          <TrendIndicator
            current={currentValue}
            previous={previousValue}
            size="sm"
          />
        )}
        {showLegacyChange && (
          <span className={`text-sm font-semibold font-body ${isPositive ? 'text-success' : isNegative ? 'text-error' : 'text-brand-gray'}`}>
            {isPositive ? '↑' : isNegative ? '↓' : ''}{Math.abs(change!).toFixed(1)}%
          </span>
        )}
      </div>
      {subtitle && (
        <span className="text-xs text-brand-gray mt-1 font-body">{subtitle}</span>
      )}
    </div>
  );
}
